import "./styles.css";
import type { AppConfig, ChatResponse } from "../shared/types";

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
  isError?: boolean;
}

const ESTIMATED_WORDS_PER_SECOND = 2.4;
const METADATA_WAIT_TIMEOUT_MS = 8000;
const GLYPH_FADE_MS = 420;
const WARNING_ICON = "!";

const messageList = requireElement("message-list");
const chatForm = requireElement<HTMLFormElement>("chat-form");
const messageInput = requireElement<HTMLTextAreaElement>("message-input");
const sendButton = requireElement<HTMLButtonElement>("send-button");
const newChatButton = requireElement<HTMLButtonElement>("new-chat-button");
const appTitle = requireElement("app-title");
const currentSurveySlug = readSurveySlugFromPath();

void initializeApp();

async function initializeApp(): Promise<void> {
  try {
    const config = await fetchJson<AppConfig>(apiPath("/api/app-config"));
    appTitle.textContent = config.appName;
    document.title =
      config.survey.name === "Survey"
        ? config.appName
        : `${config.appName} · ${config.survey.name}`;
  } catch {
    appTitle.textContent = "Clio";
  }

  chatForm.addEventListener("submit", (event) => {
    event.preventDefault();
    void submitMessage();
  });

  newChatButton.addEventListener("click", resetChat);
  messageInput.addEventListener("input", resizeInput);
  messageInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submitMessage();
    }
  });
}

async function submitMessage(): Promise<void> {
  const message = messageInput.value.trim();
  if (message === "") {
    return;
  }

  setComposerEnabled(false);
  appendMessage({ role: "user", text: message });
  messageInput.value = "";
  resizeInput();
  const loadingMessage = appendLoadingMessage();

  try {
    const response = await fetchJson<ChatResponse>(apiPath("/api/chat"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message }),
    });

    loadingMessage.remove();
    appendAssistantResponse(response);
  } catch {
    loadingMessage.remove();
    appendMessage({
      role: "assistant",
      text: "Something went wrong. Please try again.",
      isError: true,
    });
  } finally {
    setComposerEnabled(true);
    messageInput.focus();
  }
}

function appendAssistantResponse(response: ChatResponse): HTMLElement {
  if (response.audioUrl === null) {
    return appendMessage({ role: "assistant", text: response.response });
  }

  return appendAudioMessage(response.response, response.audioUrl);
}

function appendMessage(message: ChatMessage): HTMLElement {
  const article = createMessageArticle(message.role, message.isError === true);
  const bubble = document.createElement("div");
  bubble.className = "bubble";

  const paragraph = document.createElement("p");
  paragraph.textContent = message.text;
  bubble.append(paragraph);

  article.append(bubble);
  messageList.append(article);
  article.scrollIntoView({ behavior: "smooth", block: "end" });
  return article;
}

function appendAudioMessage(text: string, audioUrl: string): HTMLElement {
  const article = createMessageArticle("assistant", false);
  const bubble = document.createElement("div");
  bubble.className = "bubble audio-bubble";

  const paragraph = document.createElement("p");
  paragraph.className = "revealed-response";
  paragraph.setAttribute("aria-live", "polite");
  paragraph.textContent = "";

  const audio = document.createElement("audio");
  audio.preload = "metadata";
  audio.src = audioUrl;

  const playbackUi = createPlaybackUi();
  bubble.append(paragraph, playbackUi.wrapper, audio);
  article.append(bubble);
  messageList.append(article);
  article.scrollIntoView({ behavior: "smooth", block: "end" });

  bindPlaybackUi(audio, playbackUi, paragraph, text);
  void autoplayResponse(audio, playbackUi, paragraph, text);
  return article;
}

function createMessageArticle(role: ChatMessage["role"], isError: boolean): HTMLElement {
  const article = document.createElement("article");
  article.className = `message ${role}-message${isError ? " error-message" : ""}`;

  if (role === "assistant") {
    article.append(createAvatar());
  }

  return article;
}

function createAvatar(): HTMLImageElement {
  const avatar = document.createElement("img");
  avatar.className = "avatar";
  avatar.src = "/brand/clio-compass-logo.png";
  avatar.alt = "";
  avatar.setAttribute("aria-hidden", "true");
  return avatar;
}

function createPlaybackUi(): {
  wrapper: HTMLElement;
  status: HTMLElement;
  progress: HTMLElement;
  warning: HTMLElement;
} {
  const wrapper = document.createElement("div");
  wrapper.className = "audio-wrapper elegant-audio";

  const meter = document.createElement("div");
  meter.className = "audio-meter";
  meter.setAttribute("aria-hidden", "true");

  const progress = document.createElement("div");
  progress.className = "audio-progress";
  meter.append(progress);

  const warning = document.createElement("span");
  warning.className = "audio-warning";
  warning.textContent = WARNING_ICON;
  warning.setAttribute("role", "img");
  warning.setAttribute("aria-label", "Audio warning");
  warning.hidden = true;

  const status = document.createElement("p");
  status.className = "audio-status";
  status.textContent = "";

  wrapper.append(meter, warning, status);
  return { wrapper, status, progress, warning };
}

function bindPlaybackUi(
  audio: HTMLAudioElement,
  playbackUi: ReturnType<typeof createPlaybackUi>,
  paragraph: HTMLElement,
  text: string,
): void {
  audio.addEventListener("timeupdate", () => {
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      playbackUi.progress.style.width = `${(audio.currentTime / audio.duration) * 100}%`;
    }
  });

  audio.addEventListener("ended", () => {
    playbackUi.status.textContent = "";
    playbackUi.progress.style.width = "100%";
  });

  audio.addEventListener("error", () => {
    showAudioWarning(playbackUi, "Audio could not be played.");
    showTextImmediately(paragraph, text);
  });
}

async function autoplayResponse(
  audio: HTMLAudioElement,
  playbackUi: ReturnType<typeof createPlaybackUi>,
  paragraph: HTMLElement,
  text: string,
): Promise<void> {
  playbackUi.status.textContent = "Preparing audio…";

  try {
    audio.src = await fetchAudioObjectUrl(audio.src);
    await waitForAudioMetadata(audio, METADATA_WAIT_TIMEOUT_MS);
    await playResponse(audio, playbackUi, paragraph, text);
  } catch (error) {
    showAudioWarning(playbackUi, readErrorMessage(error));
    showTextImmediately(paragraph, text);
  }
}

async function playResponse(
  audio: HTMLAudioElement,
  playbackUi: ReturnType<typeof createPlaybackUi>,
  paragraph: HTMLElement,
  text: string,
): Promise<void> {
  playbackUi.progress.style.width = "0%";

  try {
    await audio.play();
    playbackUi.status.textContent = "";
    revealTextOverTime(paragraph, text, getRevealDurationMs(audio, text));
  } catch (error) {
    showAudioWarning(playbackUi, readErrorMessage(error));
    showTextImmediately(paragraph, text);
  }
}

function showTextImmediately(paragraph: HTMLElement, text: string): void {
  paragraph.textContent = text;
  paragraph.removeAttribute("aria-label");
  window.setTimeout(() => {
    paragraph.scrollIntoView({ behavior: "smooth", block: "end" });
  }, 0);
}

function revealTextOverTime(paragraph: HTMLElement, text: string, durationMs: number): void {
  paragraph.textContent = "";
  paragraph.setAttribute("aria-label", text);

  const glyphs = [...text];
  const revealUnits = createRevealUnits(text);
  const delayStepMs = glyphs.length === 0 ? 0 : durationMs / glyphs.length;
  const fragment = document.createDocumentFragment();
  let glyphIndex = 0;

  revealUnits.forEach((word) => {
    const wordSpan = document.createElement("span");
    wordSpan.className = "reveal-word";
    wordSpan.setAttribute("aria-hidden", "true");

    [...word].forEach((glyph) => {
      const glyphSpan = document.createElement("span");
      glyphSpan.className = glyph.trim() === "" ? "reveal-space" : "reveal-glyph";
      glyphSpan.textContent = glyph;
      glyphSpan.style.setProperty("--glyph-delay", `${glyphIndex * delayStepMs}ms`);
      glyphSpan.style.setProperty("--glyph-duration", `${GLYPH_FADE_MS}ms`);
      wordSpan.append(glyphSpan);
      glyphIndex += 1;
    });

    fragment.append(wordSpan);
  });

  paragraph.append(fragment);
  window.setTimeout(() => {
    paragraph.scrollIntoView({ behavior: "smooth", block: "end" });
  }, durationMs);
}

function createRevealUnits(text: string): string[] {
  const units = text.match(/\S+\s*/g) ?? [text];

  return units.reduce<string[]>((mergedUnits, unit) => {
    const trimmedUnit = unit.trim();
    const previousUnit = mergedUnits.at(-1);

    if (previousUnit !== undefined && /^[,.;:!?)]/.test(trimmedUnit)) {
      mergedUnits[mergedUnits.length - 1] = `${previousUnit.trimEnd()}${unit.trimStart()}`;
      return mergedUnits;
    }

    mergedUnits.push(unit);
    return mergedUnits;
  }, []);
}

function getRevealDurationMs(audio: HTMLAudioElement, text: string): number {
  if (Number.isFinite(audio.duration) && audio.duration > 0) {
    return audio.duration * 500;
  }

  return estimateRevealDurationMs(text);
}

function estimateRevealDurationMs(text: string): number {
  const wordCount = text.match(/\S+/g)?.length ?? 1;
  return (wordCount / ESTIMATED_WORDS_PER_SECOND) * 500;
}

function waitForAudioMetadata(audio: HTMLAudioElement, timeoutMs: number): Promise<void> {
  if (Number.isFinite(audio.duration) && audio.duration > 0) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("Timed out waiting for audio metadata."));
    }, timeoutMs);

    const handleLoadedMetadata = (): void => {
      cleanup();
      resolve();
    };

    const handleError = (): void => {
      cleanup();
      reject(new Error("Audio metadata failed to load."));
    };

    const cleanup = (): void => {
      window.clearTimeout(timeout);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("error", handleError);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata, { once: true });
    audio.addEventListener("error", handleError, { once: true });
    audio.load();
  });
}

function appendLoadingMessage(): HTMLElement {
  const article = createMessageArticle("assistant", false);
  article.classList.add("loading-message");

  const bubble = document.createElement("div");
  bubble.className = "bubble";
  bubble.innerHTML = `<p><span class="typing-dot"></span><span class="typing-dot"></span><span class="typing-dot"></span></p>`;

  article.append(bubble);
  messageList.append(article);
  article.scrollIntoView({ behavior: "smooth", block: "end" });
  return article;
}

function resetChat(): void {
  messageList.innerHTML = "";
  appendMessage({
    role: "assistant",
    text: "Hello. Ask me a question and I’ll do my best to answer.",
  });
  messageInput.value = "";
  resizeInput();
  messageInput.focus();
}

function resizeInput(): void {
  messageInput.style.height = "auto";
  messageInput.style.height = `${Math.min(messageInput.scrollHeight, 160)}px`;
}

function setComposerEnabled(isEnabled: boolean): void {
  messageInput.disabled = !isEnabled;
  sendButton.disabled = !isEnabled;
}

function apiPath(path: string): string {
  const url = new URL(path, window.location.origin);
  if (currentSurveySlug !== null) {
    url.searchParams.set("survey", currentSurveySlug);
  }
  return `${url.pathname}${url.search}`;
}

function readSurveySlugFromPath(): string | null {
  const segment = window.location.pathname.split("/").filter(Boolean)[0] ?? "";
  return segment === "" ? null : decodeURIComponent(segment).toLowerCase();
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchAudioObjectUrl(url: string): Promise<string> {
  const response = await fetch(url, { headers: { Accept: "audio/mpeg" } });
  if (!response.ok) {
    throw new Error(await readAudioErrorMessage(response));
  }

  return URL.createObjectURL(await response.blob());
}

async function readAudioErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.clone().json()) as unknown;
    if (isRecord(body) && typeof body.error === "string") {
      return body.error;
    }
  } catch {
    const text = await response.text().catch(() => "");
    if (text.trim() !== "") {
      return text.trim();
    }
  }

  return `Audio request failed: ${response.status}`;
}

function showAudioWarning(playbackUi: ReturnType<typeof createPlaybackUi>, message: string): void {
  playbackUi.status.textContent = "Audio unavailable.";
  playbackUi.warning.title = message;
  playbackUi.warning.hidden = false;
}

function readErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireElement<T extends HTMLElement = HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (element === null) {
    throw new Error(`Missing element #${id}`);
  }
  return element as T;
}
